import { Button, Card, Grid, Modal } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { utils as wdUtils } from 'washday-sdk';
import CreateEditForm from '../../Customers/CreateEditForm';
import LoaderOverlay from '../../../components/LoaderOverlay/LoaderOverlay';
import { trackEvent } from '../../../utils/monitoring/posthog';
import { MonitoringEventTypes } from '../../../utils/monitoring/Monitoring_Events_Enum';
import { isPossiblePhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';
import { useCreateCustomer } from '../../../hooks/customers/mutations';
import { useSelector } from 'react-redux';
import { usePostHog } from 'posthog-js/react';
import { useForm } from '@mantine/form';
import {
  emailFormValidator,
  requiredFieldFormValidator
} from '../../../utils/validators/formValidators';
import { showNotification } from '../../../utils/Notification/Notification';
import { ICustomer } from '../../../interfaces/Customer';
import { useLocation } from 'react-router-dom';
import { stripLegacyAddressCoordinates } from '../../../utils/customerAddress';
import { getFrequentCustomerCardFormValues, getFrequentCustomerCardPayload } from '../../Customers/frequentCustomerCardForm';
import { hasBackendErrors } from '../../../utils/Notification/BackendErrorsHandler';

const AddCustomerModal = ({
  opened,
  onClose,
  afterSubmit
}: {
  opened: boolean;
  onClose: () => void;
  afterSubmit: (customer: ICustomer) => void;
}) => {
  const { t } = useTranslation([
    'NewOrder',
    'FieldName',
    'CustomersPage',
    'FormValidator',
    'CommonButtons'
  ]);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const posthog = usePostHog();
  const { company } = useSelector((state) => state.user);
  const { mutateAsync: mutateAsyncCreate, isPending: isCreatePending } = useCreateCustomer();

  const form = useForm({
    initialValues: {
      name: '',
      frequentCustomerCard: getFrequentCustomerCardFormValues(),
      lastName: '',
      fullPhoneNumber: '',
      phone: '',
      phoneCountryCode: '',
      phoneCountryName: company?.country?.iso,
      email: '',
      address: {
        formattedAddress: '', // A field to store the full address as a single string
        street: '',
        neighborhood: '',
        postalCode: '',
        city: '',
        state: '',
        country: '',
        mapLocation: null
      },
      notes: '',
      privateNotes: '',
      discount: 0,
      credit: 0,
      rfc: '',
      invoiceInfo: {
        legal_name: '',
        tax_system: '',
        zipCode: '',
        applyIsrRetention: false
      },
      isActive: true,
      createdIn: searchParamStoreId
    },
    validate: {
      name: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      email: (value) => {
        if (value === '') return null;
        const emailValidatorRes = emailFormValidator(value);
        if (emailValidatorRes) return emailValidatorRes;
      },
      fullPhoneNumber: (value, formValues) => {
        try {
          if (!value) return null;
          if (!isPossiblePhoneNumber(value, formValues.phoneCountryName)) {
            showNotification({ type: 'Error', title: 'Numero telefonico no valido' });
            return 'Numero telefonico no valido';
          }
        } catch (e) {
          showNotification({ type: 'Error', title: 'Numero telefonico no valido' });
        }
      }
    }
  });

  type FormValues = typeof form.values;

  const submit = async (values: FormValues) => {
    try {
      const rawPhoneNumber = values.fullPhoneNumber;
      const selectedCountry = values.phoneCountryName || company?.country?.iso || 'MX';
      const submitValues = {
        ...values,
        frequentCustomerCard: getFrequentCustomerCardPayload(values.frequentCustomerCard),
        email: values.email.trim().toLowerCase(),
        createdIn: searchParamStoreId
      };
      if (!submitValues.email) {
        delete submitValues.email;
      }
      const newFormattedFullAddress = wdUtils.getFormattedAddress(values.address);
      if (submitValues.address && newFormattedFullAddress) {
        submitValues.address = {
          ...submitValues.address,
          formattedAddress: newFormattedFullAddress
        };
      }
      submitValues.discount = values.discount || 0;
      submitValues.credit = values.credit || 0;
      try {
        if (!rawPhoneNumber) {
          submitValues.phone = '';
          submitValues.phoneCountryCode = '';
          submitValues.phoneCountryName = '';
        } else {
          const phoneNumber = parsePhoneNumberFromString(rawPhoneNumber, selectedCountry);
          if (phoneNumber) {
            submitValues.phoneCountryCode = phoneNumber.countryCallingCode;
            submitValues.phoneCountryName = phoneNumber.country;
            submitValues.phone = phoneNumber.nationalNumber;
          } else {
            //means the phone number is not valid for that country selected
            console.error('Phone number could not be parsed');
            // Handle invalid phone number case
            form.setFieldValue('phoneCountryCode', '');
            form.setFieldValue('phoneCountryName', '');
            submitValues.phoneCountryCode = '';
            submitValues.phoneCountryName = '';
            submitValues.phone = '';
          }
        }
      } catch (error) {
        console.error('Error parsing phone number:', error);
        submitValues.phoneCountryCode = '';
        submitValues.phoneCountryName = '';
        submitValues.phone = '';
      }
      const { fullPhoneNumber, ...cleanSubmitValues } =
        stripLegacyAddressCoordinates(submitValues);
      const customer = await mutateAsyncCreate(cleanSubmitValues);
      trackEvent(posthog, MonitoringEventTypes.POS_PAGE_CUSTOMER_CREATED_SUCCESS, {});
      showNotification({ type: 'Default', title: t('Notifications:success') });
      form.reset();
      afterSubmit(customer);
    } catch (e) {
      trackEvent(posthog, MonitoringEventTypes.POS_PAGE_CUSTOMER_CREATED_FAIL, {
        error: e,
        data: values
      });
      if (!hasBackendErrors(e)) {
        showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      }
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t('NewOrder:addCustomer')}>
      <LoaderOverlay visible={isCreatePending} />
      <form onSubmit={form.onSubmit(submit)}>
        <CreateEditForm storeId={searchParamStoreId} form={form} />
        <Card pos={'sticky'} bottom={5}>
          <Card.Section inheritPadding py="xs">
            <Grid align="center">
              <Grid.Col span={{ xs: 12, md: 6 }}>
                <Button
                  fullWidth
                  color="gray"
                  loading={isCreatePending}
                  disabled={isCreatePending}
                  onClick={onClose}>
                  {t('CommonButtons:cancel')}
                </Button>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, md: 6 }}>
                <Button fullWidth type="submit" loading={isCreatePending}>
                  {t('CommonButtons:submit')}
                </Button>
              </Grid.Col>
            </Grid>
          </Card.Section>
        </Card>
      </form>
    </Modal>
  );
};

export default AddCustomerModal;
